// src/utils/resourcesHelper.js
import { PREFERRED_CATEGORIES, DEFAULT_CATEGORY_CONFIG } from '../config/resourceCategories';
import { FileText } from 'lucide-react';

/**
 * Format file size from bytes to human-readable format
 * @param {number} bytes - File size in bytes
 * @returns {string} Formatted file size (e.g., "2.5 MB" or "345 KB")
 */
export const formatFileSize = (bytes) => {
  if (!bytes || bytes === 0) return 'Unknown size';
  
  const mb = bytes / 1024 / 1024;
  if (mb >= 1) return `${mb.toFixed(1)} MB`;
  
  const kb = bytes / 1024;
  return `${kb.toFixed(0)} KB`;
};

/**
 * Filter resources by search term
 * Searches across: title, description, fileName, and categories
 * @param {Array} resources - Array of resource objects
 * @param {string} searchTerm - Search query
 * @returns {Array} Filtered resources
 */
export const filterResourcesBySearch = (resources, searchTerm) => {
  if (!searchTerm || !searchTerm.trim()) return resources;
  
  const term = searchTerm.toLowerCase().trim();
  
  return resources.filter(resource => {
    // Search in title (supports both Arabic and English)
    const titleMatch = resource.title?.toLowerCase().includes(term) || false;
    
    // Search in description
    const descMatch = resource.description?.toLowerCase().includes(term) || false;
    
    // Search in filename
    const fileMatch = resource.fileName?.toLowerCase().includes(term) || false;
    
    // Search in categories
    const categoryMatch = resource.categories?.some(cat => 
      cat.toLowerCase().includes(term)
    ) || false;
    
    return titleMatch || descMatch || fileMatch || categoryMatch;
  });
};

/**
 * Build display categories with hybrid system:
 * 1. Show preferred categories that have files (hide empty ones)
 * 2. Show discovered categories (not in preferred list) alphabetically
 * 3. Add "All" category at the beginning
 * 
 * @param {Array} resources - Array of resource objects
 * @returns {Array} Display categories with counts
 */
export const buildDisplayCategories = (resources) => {
  if (!resources || resources.length === 0) {
    // No resources - return just "All" with 0 count
    return [{
      id: 'all',
      label: 'All',
      icon: FileText,
      color: 'blue',
      order: 0,
      count: 0
    }];
  }

  // Get all unique categories actually used in resources
  const categoriesInUse = [...new Set(
    resources.flatMap(r => r.categories || [])
  )];
  
  // Filter preferred categories to only those with files (hide empty ones)
  const activePreferredCategories = PREFERRED_CATEGORIES
    .filter(prefCat => categoriesInUse.includes(prefCat.label))
    .map(cat => ({
      ...cat,
      count: resources.filter(r => 
        r.categories && r.categories.includes(cat.label)
      ).length
    }))
    .sort((a, b) => a.order - b.order);
  
  // Find discovered categories (not in preferred list)
  const preferredLabels = PREFERRED_CATEGORIES.map(p => p.label);
  const discoveredCategories = categoriesInUse
    .filter(cat => !preferredLabels.includes(cat))
    .sort() // Alphabetical order
    .map(cat => ({
      id: cat.toLowerCase().replace(/\s+/g, '-'),
      label: cat,
      icon: DEFAULT_CATEGORY_CONFIG.icon,
      color: DEFAULT_CATEGORY_CONFIG.color,
      order: DEFAULT_CATEGORY_CONFIG.order,
      count: resources.filter(r => 
        r.categories && r.categories.includes(cat)
      ).length
    }));
  
  // Combine: "All" first, then preferred categories, then discovered
  return [
    {
      id: 'all',
      label: 'All',
      icon: FileText,
      color: 'blue',
      order: 0,
      count: resources.length
    },
    ...activePreferredCategories,
    ...discoveredCategories
  ];
};

/**
 * Get category configuration for styling
 * @param {string} categoryLabel - Category label to look up
 * @returns {Object} Category config with icon, color, order
 */
export const getCategoryConfig = (categoryLabel) => {
  const found = PREFERRED_CATEGORIES.find(cat => cat.label === categoryLabel);
  return found || DEFAULT_CATEGORY_CONFIG;
};

/**
 * Count resources in a specific category
 * @param {Array} resources - Array of resource objects
 * @param {string} categoryLabel - Category to count
 * @returns {number} Number of resources in category
 */
export const countResourcesByCategory = (resources, categoryLabel) => {
  if (!resources || !categoryLabel) return 0;
  return resources.filter(r => 
    r.categories && r.categories.includes(categoryLabel)
  ).length;
};

/**
 * Get color classes for category badges and buttons
 * @param {string} color - Color name from category config
 * @param {boolean} isActive - Whether the category is currently selected
 * @returns {string} Tailwind CSS classes
 */
export const getCategoryColorClasses = (color, isActive = false) => {
  const colorMap = {
    blue: isActive 
      ? 'bg-blue-500 text-white' 
      : 'bg-blue-100 text-blue-700 hover:bg-blue-200',
    purple: isActive 
      ? 'bg-purple-500 text-white' 
      : 'bg-purple-100 text-purple-700 hover:bg-purple-200',
    indigo: isActive 
      ? 'bg-indigo-500 text-white' 
      : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200',
    pink: isActive 
      ? 'bg-pink-500 text-white' 
      : 'bg-pink-100 text-pink-700 hover:bg-pink-200',
    green: isActive 
      ? 'bg-green-500 text-white' 
      : 'bg-green-100 text-green-700 hover:bg-green-200',
    orange: isActive 
      ? 'bg-orange-500 text-white' 
      : 'bg-orange-100 text-orange-700 hover:bg-orange-200',
    gray: isActive 
      ? 'bg-gray-500 text-white' 
      : 'bg-gray-100 text-gray-700 hover:bg-gray-200',
  };
  
  return colorMap[color] || colorMap.gray;
};